import { Fragment, useEffect } from 'react'
import { useAuthState, useQuery } from 'packages/core'
import { Switch, Route, Redirect } from 'react-router-dom'
import withRequiredData from './withRequiredData'
import { PageContextProvider } from 'pageContext'
import { setIsWorkflowRenderable } from 'packages/core/auth/actions'
import {
    RequestViewMyRequests,
    RequestViewMyTasks,
    RequestViewAllRequests,
} from 'pages/Requests'
import {
    ItemViewMyRequests,
    ItemViewMyTasks,
    ItemViewAllRequests,
} from 'pages/RequestItems'

import {
    MyRequestsWorkflows,
    MyTasksWorkflows,
    AllWorkflows,
} from 'pages/CollaborationTasks'
import useSubcomponents from 'useSubcomponents'
import config from 'config'
import { getWorkflowWaitTimeInSeconds } from 'packages/core'
import { useAppState } from 'appContext'
import { IdleTimer } from 'packages/eid-ui'
import { useSessionTimeout } from 'hooks'

const App = () => {
    const [{ isWorkflowIRenderable }, authDispatch] = useAuthState()
    const workflowWaitTime = getWorkflowWaitTimeInSeconds() * 1000
    const { data: loginSessionTimeout } = useSessionTimeout()
    const [, dispatch] = useAppState()
    const query = useQuery()
    useEffect(() => {
        if (!isWorkflowIRenderable) {
            setTimeout(() => {
                authDispatch(setIsWorkflowRenderable(true))
            }, workflowWaitTime)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (config.ENABLE_DEV_FEATURES?.toLowerCase() === 'true') {
            dispatch({
                type: 'ENABLE_DEV_FEATURES',
            })
        }
        if (query.get('enableDevFeatures')) {
            query.delete('enableDevFeatures')
            dispatch({
                type: 'ENABLE_DEV_FEATURES',
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const {
        hasAccessToRequestViewMyRequestsPage,
        hasAccessToItemViewMyRequestsPage,
        hasAccessToWorkflowsMyRequestsPage,
        hasAccessToRequestViewMyTasksPage,
        hasAccessToItemViewMyTasksPage,
        hasAccessToWorkflowsMyTasksPage,
        hasAccessToRequestViewAllRequestsPage,
        hasAccessToItemViewAllRequestsPage,
        hasAccessToWorkflowsAllRequestsPage,
    } = useSubcomponents()

    const defaultRedirectTo = hasAccessToRequestViewMyTasksPage
        ? `${config.APP_SUBPATH}/myTasks/businessRequests`
        : hasAccessToItemViewMyTasksPage
        ? `${config.APP_SUBPATH}/myTasks/businessRequestItems`
        : hasAccessToWorkflowsMyTasksPage
        ? `${config.APP_SUBPATH}/myTasks/collaborationTasks`
        : hasAccessToRequestViewMyRequestsPage
        ? `${config.APP_SUBPATH}/myRequests/businessRequests`
        : hasAccessToItemViewMyRequestsPage
        ? `${config.APP_SUBPATH}/myRequests/businessRequestItems`
        : hasAccessToWorkflowsMyRequestsPage
        ? `${config.APP_SUBPATH}/myRequests/collaborationTasks`
        : hasAccessToRequestViewAllRequestsPage
        ? `${config.APP_SUBPATH}/all/businessRequests`
        : hasAccessToItemViewAllRequestsPage
        ? `${config.APP_SUBPATH}/all/businessRequestItems`
        : hasAccessToWorkflowsAllRequestsPage
        ? `${config.APP_SUBPATH}/all/collaborationTasks`
        : `/forbidden`

    return (
        <Fragment>
            {!isWorkflowIRenderable && (
                <div style={{ display: 'none' }}>
                    <iframe
                        title={`EmpowerID`}
                        src={`${config.BASE_EID_URL}/UI/Common/SAML2/HiddenBlankPage`}
                    />
                </div>
            )}

            <Switch>
                <Redirect exact from={`/`} to={defaultRedirectTo} />
                <Redirect
                    exact
                    from={`${config.APP_SUBPATH}/`}
                    to={defaultRedirectTo}
                />

                {hasAccessToRequestViewMyRequestsPage && (
                    <Route
                        exact
                        path={`${config.APP_SUBPATH}/myRequests/businessRequests`}
                        render={(props) => (
                            <PageContextProvider key="RequestViewMyRequests">
                                <RequestViewMyRequests {...props} />
                            </PageContextProvider>
                        )}
                    />
                )}
                {hasAccessToRequestViewMyTasksPage && (
                    <Route
                        exact
                        path={`${config.APP_SUBPATH}/myTasks/businessRequests`}
                        render={(props) => (
                            <PageContextProvider key="RequestViewMyTasks">
                                <RequestViewMyTasks {...props} />
                            </PageContextProvider>
                        )}
                    />
                )}
                {hasAccessToRequestViewAllRequestsPage && (
                    <Route
                        exact
                        path={`${config.APP_SUBPATH}/all/businessRequests`}
                        render={(props) => (
                            <PageContextProvider key="RequestViewAllRequests">
                                <RequestViewAllRequests {...props} />
                            </PageContextProvider>
                        )}
                    />
                )}

                {hasAccessToItemViewMyRequestsPage && (
                    <Route
                        exact
                        path={`${config.APP_SUBPATH}/myRequests/businessRequestItems`}
                        render={(props) => (
                            <PageContextProvider key="ItemViewMyRequests">
                                <ItemViewMyRequests {...props} />
                            </PageContextProvider>
                        )}
                    />
                )}
                {hasAccessToItemViewMyTasksPage && (
                    <Route
                        exact
                        path={`${config.APP_SUBPATH}/myTasks/businessRequestItems`}
                        render={(props) => (
                            <PageContextProvider key="ItemViewMyTasks">
                                <ItemViewMyTasks {...props} />
                            </PageContextProvider>
                        )}
                    />
                )}
                {hasAccessToItemViewAllRequestsPage && (
                    <Route
                        exact
                        path={`${config.APP_SUBPATH}/all/businessRequestItems`}
                        render={(props) => (
                            <PageContextProvider key="ItemViewAllRequests">
                                <ItemViewAllRequests {...props} />
                            </PageContextProvider>
                        )}
                    />
                )}

                {hasAccessToWorkflowsMyRequestsPage && (
                    <Route
                        exact
                        path={`${config.APP_SUBPATH}/myRequests/collaborationTasks`}
                        render={(props) => (
                            <PageContextProvider key="CollaborationTasksMyRequests">
                                <MyRequestsWorkflows {...props} />
                            </PageContextProvider>
                        )}
                    />
                )}

                {hasAccessToWorkflowsMyTasksPage && (
                    <Route
                        exact
                        path={`${config.APP_SUBPATH}/myTasks/collaborationTasks`}
                        render={(props) => (
                            <PageContextProvider key="CollaborationTasksMyTasks">
                                <MyTasksWorkflows {...props} />
                            </PageContextProvider>
                        )}
                    />
                )}

                {hasAccessToWorkflowsAllRequestsPage && (
                    <Route
                        exact
                        path={`${config.APP_SUBPATH}/all/collaborationTasks`}
                        render={(props) => (
                            <PageContextProvider key="CollaborationTasksAll">
                                <AllWorkflows {...props} />
                            </PageContextProvider>
                        )}
                    />
                )}

                <Redirect from="" to="/notFound" />
            </Switch>
            {loginSessionTimeout && (
                <IdleTimer loginSessionTimeout={loginSessionTimeout} />
            )}
        </Fragment>
    )
}

export default withRequiredData(App)
